import React, { ElementType, ReactNode, useEffect } from 'react'
import { Route, Switch } from 'react-router-dom'

import ImpersonateHint from 'ImpersonateHint'
import RedirectPage from 'RedirectPage'
import ChangePassword from 'account/changePassword'
import EmployeeNotice from 'account/employeeNotice'
import EmployeeSignup from 'account/employeeSignup/eligibleEmployee'
import NonEligibleEmployeeSignup from 'account/employeeSignup/nonEligibleEmployee'
import LoginController from 'account/loginController'
import { Skeleton } from 'antd'
import CsiDashboard, { CSI_DASHBOARD_PATH } from 'csiDashboard'
import AppConfigurations from 'csiDashboard/appConfigurations'
import AuditSearch, { AUDIT_SEARCH_PATH } from 'csiDashboard/auditSearch'
import CompanyDetails from 'csiDashboard/csiSearch/companyDetails'
import CsiSearch, { CSI_SEARCH_PATH } from 'csiDashboard/csiSearch/searchCustomers'
import DefaultExpenseTypes from 'csiDashboard/defaultExpenseTypes'
import ViewEmailTemplates from 'csiDashboard/emailTemplates'
import AddEmailTemplate from 'csiDashboard/emailTemplates/addEmailTemplate'
import EditEmailTemplate from 'csiDashboard/emailTemplates/editEmailTemplate'
import InternalUsers, { INTERNAL_USERS_PATH } from 'csiDashboard/internalUsers'
import Migration, { MIGRATION_PATH } from 'csiDashboard/migration'
import QaUtils from 'csiDashboard/qaUtils'
import Reports, { REPORTS_PATH } from 'csiDashboard/reports'
import ScheduledJobs, { SCHEDULED_JOBS_PATH } from 'csiDashboard/scheduledJobs'
import SearchEmails, { SEARCH_EMAILS_PATH } from 'csiDashboard/searchEmails'
import SearchUserActionHistory, { SEARCH_USER_ACTION_HISTORY_PATH } from 'csiDashboard/searchUserActionHistory'
import EmailsSuppressionList, { SUPPRESSION_LIST_PATH } from 'csiDashboard/suppressionList'
import AiAnalytics, { AI_ANALYTICS_PATH } from 'docReviewDashboard/AiAnalytics'
import JsonContent, { JSON_PATH } from 'docReviewDashboard/AiAnalytics/JsonContent'
import AddCannedMessages from 'docReviewDashboard/CannedMessages/addCannedMessages/addCannedMessages'
import ViewCannedMessages from 'docReviewDashboard/CannedMessages/viewCannedMessages/viewCannedMessages'
import DocReviewDashboardWrapper from 'docReviewDashboard/DocReviewDashboardWrapper'
import Expenses, { EXPENSES_PATH } from 'docReviewDashboard/Expenses'
import Home, { HOME_PATH } from 'docReviewDashboard/Home'
import KeywordManagement, { KEYWORD_MANAGEMENT } from 'docReviewDashboard/KeywordManagement'
import Premiums from 'docReviewDashboard/Premiums'
import SearchExpenses, { SEARCH_EXPENSES_PATH } from 'docReviewDashboard/searchExpenses/index'
import SearchPremiums from 'docReviewDashboard/searchPremiums'
import EmployeeSignupFlow from 'employeeSignup/eligibleEmployeeSignup'
import NonEligibleEmployeeSignupFlow from 'employeeSignup/nonEligibleEmployeeSignup'
import IchraDecisionTools from 'ichraDecisionTool/Routes'
import { SIGNUP_COMPLETE_PATH } from 'ichraDecisionTool/prospectSignup/SignupComplete'
import NonEligibleEmployeeDashboard from 'nonEligibleEmployeeDashboard'
import { addInspectlet } from 'scripts/addInspectlet'
import { loadPendo } from 'scripts/loadPendo'
import { loadZendeskWidget } from 'scripts/loadZendeskWidget'

import getLoginUser, { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'
import config from 'core/application/commons/config'
import { sessionStore } from 'core/application/commons/localStorage'

import useLoginUser from 'common/hooks/useLoginUser'
import useQueryRequest from 'common/hooks/useQueryRequest'

import NotFound from 'errorPages/NotFound'

import ForgetPasswordComponent from './account/forgetPassword/ForgetPassword'
import InstructionsSent from './account/forgetPassword/InstructionsSent'
import PasswordWrapper from './account/forgetPassword/PaswordWrapper'
import ResetPassword from './account/forgetPassword/ResetPassword'
import Login from './account/login/Login'
import SetNewQsehraPassword from './account/setNewQsehraPassword'
import Signup from './account/signup/Signup'
import AdminDashboard from './adminDashboard'
import AdminSignUp from './adminSignUp'
import useSharedState, { SharedStateKeys } from './common/hooks/useSharedState'
import { ROUTE_PATHS } from './common/routePaths'
import EligibleEmployeeDashboard from './employeeDashboard'

import './App.less'
import 'antd/dist/reset.css'

interface AppProps {}

const isProduction = window.location.href.includes('app.peoplekeep.com')

const wrapComponent = (Cmp: React.ElementType, Wrapper: React.ElementType) => () =>
  (
    <Wrapper>
      <Cmp />
    </Wrapper>
  )

type RouteType = {
  path: string
  component: any
  exact?: boolean
  children?: ReactNode
  layout?: ElementType
}
const routes: RouteType[] = [{ path: ROUTE_PATHS.SIGNUP_PATH, component: Signup }]

const App: React.FC<AppProps> = () => {
  const [, setIsProduction] = useSharedState<boolean>(SharedStateKeys.IsProduction)

  useEffect(() => {
    recurly.configure({ publicKey: config.recurlyKey })
  }, [])

  useEffect(() => {
    setIsProduction(isProduction)
  }, [])

  return (
    <Skeleton loading={false} active>
      <ImpersonateHint />
      <Switch>
        <Route exact path="/" component={RedirectPage} />
        <Route path={ROUTE_PATHS.SIGNUP_PATH} component={Signup} />
        <Route path={ROUTE_PATHS.PASSWORD_RESET_REQUIRED_PATH} component={SetNewQsehraPassword} />
        <Route exact path={ROUTE_PATHS.LOGIN_PATH} component={Login} />
        <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_CREATE_ACCOUNT} component={EmployeeSignup} />
        <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_SIGNUP_FLOW} component={EmployeeSignupFlow} />
        <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_SIGNUP} component={NonEligibleEmployeeSignup} />
        <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_SIGNUP_FLOW} component={NonEligibleEmployeeSignupFlow} />
        <Route
          path={`${ROUTE_PATHS.FORGOT_PASSWORD_PATH}/sent/:emailValue`}
          component={wrapComponent(InstructionsSent, PasswordWrapper)}
        />
        <Route
          path={`${ROUTE_PATHS.FORGOT_PASSWORD_PATH}/reset-password`}
          component={wrapComponent(ResetPassword, PasswordWrapper)}
        />
        <Route
          path={`${ROUTE_PATHS.FORGOT_PASSWORD_PATH}/:emailValue?`}
          component={wrapComponent(ForgetPasswordComponent, PasswordWrapper)}
        />
        <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_NOTICE} component={EmployeeNotice} />
        <Route path={``} component={() => <PrivateRoutes />} />
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Skeleton>
  )
}

const PrivateRoutes = () => {
  const [user] = useQueryRequest(null, () => getLoginUser())
  const [loginUser, setLoginUser] = useLoginUser()

  const permissions = user ? user.permissions : []

  useEffect(() => {
    setLoginUser(user)

    if (user && shoudDisplayZendeskWidget()) {
      loadZendeskWidget(config.zendeskKey[user.productId])
    }

    if (shouldRecordInspectletSession(user)) {
      addInspectlet(config.inspectletKey, user.email)
    }

    if (user && isProduction) {
      loadPendo(user)
    }
  }, [user])

  const isOnInternalTools = (): boolean => {
    const location = window.location.href
    return (
      location.includes(ROUTE_PATHS.ICHRA_DECISION_TOOL_PATH) ||
      location.includes(ROUTE_PATHS.PROSPECT_WELCOME_PATH) ||
      location.includes(ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH) ||
      location.includes(ROUTE_PATHS.CSI_DASHBOARD_PATH) ||
      location.includes(ROUTE_PATHS.CHOOSE_APP)
    )
  }

  const shoudDisplayZendeskWidget = (): boolean => {
    const location = window.location.href

    return !(isOnInternalTools() || location.includes(SIGNUP_COMPLETE_PATH))
  }

  const shouldRecordInspectletSession = (user: User): boolean => {
    const location = window.location.href
    const routeIsAllowedToBeRecorded = !isOnInternalTools()

    return (
      (config.env === 'production' || config.env === 'QA') &&
      !sessionStore.getSessionToken() &&
      user !== null &&
      routeIsAllowedToBeRecorded
    )
  }

  return (
    <>
      {loginUser && (
        <>
          <Route path={ROUTE_PATHS.ADMIN_SIGNUP_PATH} component={AdminSignUp} />
          <Route path={ROUTE_PATHS.ADMIN_DASHBOARD_PATH} component={AdminDashboard} />
          <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_SIGNUP_FLOW} component={NonEligibleEmployeeSignupFlow} />
          <Route path={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD} component={EligibleEmployeeDashboard} />
          <Route path={ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_DASHBOARD} component={NonEligibleEmployeeDashboard} />
          {/* This will be changed later to
               <Route path={ROUTE_PATHS.CSI_DASHBOARD_PATH} element={<CsiDashboard />}>
                  <Route path={ROUTE_PATHS.CSI_CHANGE_PASSWORD} component={ChangePassword} />
                  <Route path={ROUTE_PATHS.ADD_EMAIL_TEMPLATES} component={AddEmailTemplate} />
                  <Route path={`${ROUTE_PATHS.EDIT_EMAIL_TEMPLATES}/:templateId`} component={EditEmailTemplate} />
                  ...
                </Route>
              And therefore removing Switch and handling nested routes in the CsiDashboard component
          */}
          <Route
            path={ROUTE_PATHS.CSI_DASHBOARD_PATH}
            component={() => (
              <CsiDashboard>
                <Switch>
                  <Route path={ROUTE_PATHS.CSI_CHANGE_PASSWORD} component={ChangePassword} />
                  <Route path={ROUTE_PATHS.ADD_EMAIL_TEMPLATES} component={AddEmailTemplate} />
                  <Route path={`${ROUTE_PATHS.EDIT_EMAIL_TEMPLATES}/:templateId`} component={EditEmailTemplate} />
                  <Route
                    path={`${CSI_DASHBOARD_PATH}/${CSI_SEARCH_PATH}`}
                    component={() => (
                      <Switch>
                        <Route path={ROUTE_PATHS.CSI_SEARCH} exact component={CsiSearch} />
                        <Route path={`${ROUTE_PATHS.CSI_COMPANY_DETAILS}/:companyId`} component={CompanyDetails} />
                        <Route component={NotFound} />
                      </Switch>
                    )}
                  />
                  <Route path={`${CSI_DASHBOARD_PATH}/${SEARCH_EMAILS_PATH}`} component={SearchEmails} />
                  <Route
                    path={`${CSI_DASHBOARD_PATH}/${SEARCH_USER_ACTION_HISTORY_PATH}`}
                    component={SearchUserActionHistory}
                  />
                  <Route path={`${CSI_DASHBOARD_PATH}/${AUDIT_SEARCH_PATH}`} component={AuditSearch} />
                  <Route path={`${CSI_DASHBOARD_PATH}/${SUPPRESSION_LIST_PATH}`} component={EmailsSuppressionList} />
                  <Route path={ROUTE_PATHS.CSI_CONFIGURATIONS} component={AppConfigurations} />
                  <Route path={ROUTE_PATHS.CSI_EXPENSE_TYPES} component={DefaultExpenseTypes} />
                  <Route path={ROUTE_PATHS.CSI_QA_UTILS} component={QaUtils} />

                  {permissions.includes(Permissions.AccessScheduledJobs) && (
                    <Route path={`${CSI_DASHBOARD_PATH}/${SCHEDULED_JOBS_PATH}`} component={ScheduledJobs} />
                  )}

                  {permissions.includes(Permissions.AccessInternalUsers) && (
                    <Route path={`${CSI_DASHBOARD_PATH}/${INTERNAL_USERS_PATH}`} component={InternalUsers} />
                  )}

                  {permissions.includes(Permissions.AccessMigration) && (
                    <Route path={`${CSI_DASHBOARD_PATH}/${MIGRATION_PATH}`} component={Migration} />
                  )}

                  {permissions.includes(Permissions.AccessReports) && (
                    <Route path={`${CSI_DASHBOARD_PATH}/${REPORTS_PATH}`} component={Reports} />
                  )}

                  {permissions.includes(Permissions.AccessMigration) && (
                    <Route path={ROUTE_PATHS.EMAIL_TEMPLATES} component={ViewEmailTemplates} />
                  )}

                  <Route>
                    <NotFound />
                  </Route>
                </Switch>
              </CsiDashboard>
            )}
          />
          <Route
            path={ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}
            component={() => (
              <DocReviewDashboardWrapper user={user}>
                <Switch>
                  <Route path={ROUTE_PATHS.DOC_REVIEW_CHANGE_PASSWORD_PATH} component={ChangePassword} />
                  <Route path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${HOME_PATH}`} component={Home} />
                  <Route path={`${ROUTE_PATHS.DOC_REVIEW_PREMIUMS}/:id?`} component={Premiums} />
                  <Route path={ROUTE_PATHS.DOC_REVIEW_SEARCH_PREMIUMS} component={SearchPremiums} />
                  <Route path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${EXPENSES_PATH}/:id?`} component={Expenses} />
                  <Route
                    path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${SEARCH_EXPENSES_PATH}`}
                    component={SearchExpenses}
                  />
                  <Route
                    path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/canned-messages`}
                    component={() => (
                      <Switch>
                        <Route
                          exact
                          path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/canned-messages/add`}
                          component={AddCannedMessages}
                        />
                        <Route
                          exact
                          path="/doc-review-dashboard/canned-messages/:cannedMessageId"
                          component={AddCannedMessages}
                        />
                        <Route path="/" component={ViewCannedMessages} />
                      </Switch>
                    )}
                  />
                  <Route
                    path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${AI_ANALYTICS_PATH}/${JSON_PATH}`}
                    component={JsonContent}
                  />
                  <Route
                    path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${AI_ANALYTICS_PATH}`}
                    component={AiAnalytics}
                  />
                  <Route
                    path={`${ROUTE_PATHS.DOC_REVIEW_DASHBOARD_PATH}/${KEYWORD_MANAGEMENT}`}
                    component={KeywordManagement}
                  />
                  <Route>
                    <NotFound />
                  </Route>
                </Switch>
              </DocReviewDashboardWrapper>
            )}
          />
          <Route path={ROUTE_PATHS.ICHRA_DECISION_TOOL_PATH} component={IchraDecisionTools} />
          <Route path={ROUTE_PATHS.CHOOSE_APP} component={LoginController} />
        </>
      )}
    </>
  )
}

export default App
